import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { track } from "../../lib/analytics";

const GTM_ID = process.env.REACT_APP_GTM_ID;
const GA4_ID = process.env.REACT_APP_GA4_ID;
const FB_PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;

function injectScript(src, async = true) {
  const s = document.createElement("script");
  s.src = src;
  s.async = async;
  document.head.appendChild(s);
}

// Loads tracking providers when their IDs are configured, and wires
// conversion events: phone clicks, CTA clicks, scroll depth, route views.
const Analytics = () => {
  const location = useLocation();

  // One-time provider bootstrapping.
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];

    if (GTM_ID) {
      window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      injectScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
    }

    if (GA4_ID) {
      injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
      window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", GA4_ID, { send_page_view: true });
    }

    if (FB_PIXEL_ID && !window.fbq) {
      /* eslint-disable */
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
        if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
        t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
      })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
      /* eslint-enable */
      window.fbq("init", FB_PIXEL_ID);
      window.fbq("track", "PageView");
    }

    if (CLARITY_ID && !window.clarity) {
      /* eslint-disable */
      (function (c, l, a, r, i, t, y) {
        c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
        t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
        y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
      })(window, document, "clarity", "script", CLARITY_ID);
      /* eslint-enable */
    }
  }, []);

  // Delegated conversion listeners + scroll depth.
  useEffect(() => {
    const onClick = (e) => {
      const tel = e.target.closest('a[href^="tel:"]');
      if (tel) { track("phone_click", { href: tel.getAttribute("href") }); return; }
      const el = e.target.closest("[data-track]");
      if (el) track(el.getAttribute("data-track"), { label: el.getAttribute("data-track-label") || undefined });
    };
    document.addEventListener("click", onClick);

    const fired = new Set();
    const onScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - window.innerHeight;
      if (total <= 0) return;
      const pct = ((doc.scrollTop || document.body.scrollTop) / total) * 100;
      [25, 50, 75, 100].forEach((m) => {
        if (pct >= m && !fired.has(m)) { fired.add(m); track("scroll_depth", { percent: m }); }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      document.removeEventListener("click", onClick);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // SPA page-view tracking. Fires on every route change (delayed a tick so
  // the document.title set by the Seo component is current for GA4).
  useEffect(() => {
    const id = setTimeout(() => {
      track("page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }, 60);
    return () => clearTimeout(id);
  }, [location]);

  return null;
};

export default Analytics;
